import { Button, Typography } from "matsuri-ui"
import dayjs from "dayjs"
import { css } from "@emotion/react"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded"
import {
    BannerLines,
    Card,
    CardTitle,
    Content,
    DetailLabel,
    DetailList,
    DetailRow,
    DetailValue,
    DoneBody,
    DoneIcon,
    Main,
    ReportIdValue,
    ReportedAt,
    TitleAccent
} from "./styled"
import { ReportDetail } from "./parts/ReportDetail"
import { MobileLayout } from "./utils"
import { Header } from "@/components/Header/Header"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import { ReportFooter } from "@/components/Footer/ReportFooter"
import {
    ImageViewerOverlay,
    useImageViewerOverlay
} from "@/pages/OperationInfoPage/parts/ImageViewerOverlay"
import { LostItemReport } from "@/hooks/useLostItemReport"
import { useTranslation } from "@/hooks/useTranslation"

interface Props {
    report: LostItemReport
    listingName?: string
    layout: MobileLayout
    /** 清掃詳細から単独で開いた場合。次へ進む導線が完了報告ではなく詳細へ戻る。 */
    standalone?: boolean
    onAddAnother: () => void
    onNext: () => void
}

/**
 * 1件送信した直後の完了画面。
 * 保管場所を動かされると WORK が回収できなくなるため、そのままにしておくよう明示する。
 */
export const CompletedView = ({
    report,
    listingName,
    layout,
    standalone = false,
    onAddAnother,
    onNext
}: Props) => {
    const { t } = useTranslation()

    const { openedImage, openImageViewer, closeImageViewer } =
        useImageViewerOverlay()

    return (
        <Main style={layout.rootProps.style}>
            <Header {...layout.headerProps}>
                <MobileAppHeader
                    title={
                        <Typography variant="h4">{t("報告完了")}</Typography>
                    }
                />
            </Header>

            <div {...layout.bodyProps}>
                <DoneBody>
                    <DoneIcon>
                        <CheckCircleRoundedIcon style={{ fontSize: 44 }} />
                    </DoneIcon>
                    <Typography variant="h4" bolder>
                        {t("報告を受け付けました")}
                    </Typography>
                    {/* 1文ずつ翻訳させたいので、文ごとに t() を分ける */}
                    <BannerLines>
                        <Typography color="textSecondary">
                            {t("WORKチームが回収に向かいます。")}
                        </Typography>
                        <Typography color="textSecondary">
                            {t(
                                "保管場所を変えずにそのままにしておいてください。"
                            )}
                        </Typography>
                    </BannerLines>
                </DoneBody>

                <Content>
                    <Card elevation={1}>
                        <CardTitle>
                            <TitleAccent />
                            {t("報告した内容")}
                        </CardTitle>

                        <ReportDetail
                            category={report.category}
                            itemDescription={report.itemDescription}
                            storagePlace={report.storagePlace}
                            note={report.note}
                            photos={report.images.map(image => ({
                                id: image.id,
                                url: image.imageUrl
                            }))}
                            onOpenPhoto={photo =>
                                openImageViewer({
                                    id: photo.id,
                                    imageUrl: photo.url
                                })
                            }
                            footer={
                                <ReportedAt>
                                    <ScheduleRoundedIcon
                                        style={{ fontSize: 14 }}
                                    />
                                    {dayjs
                                        .unix(report.reportedAt)
                                        .format("M/D HH:mm")}
                                </ReportedAt>
                            }
                        />
                    </Card>

                    <Card elevation={1}>
                        <DetailList>
                            {listingName && (
                                <DetailRow>
                                    <DetailLabel>{t("物件")}</DetailLabel>
                                    <DetailValue>{listingName}</DetailValue>
                                </DetailRow>
                            )}
                            <DetailRow>
                                <DetailLabel>{t("報告ID")}</DetailLabel>
                                <ReportIdValue>{report.id}</ReportIdValue>
                            </DetailRow>
                        </DetailList>
                    </Card>
                </Content>
            </div>

            <ReportFooter {...layout.footerProps}>
                <Button
                    variant="filled"
                    color="primary"
                    fullWidth
                    onClick={onAddAnother}
                >
                    {t("もう一件報告する")}
                </Button>
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={onNext}
                    css={css`
                        margin-top: 8px;
                    `}
                >
                    {standalone ? t("清掃詳細に戻る") : t("完了報告へ進む")}
                </Button>
            </ReportFooter>

            <ImageViewerOverlay
                openedImage={openedImage}
                onClose={closeImageViewer}
            />
        </Main>
    )
}
